import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom"; 
import { 
    AppBar, Toolbar, Box, Button, Menu, MenuItem, 
    Container, Stack, Typography, Divider, IconButton, Drawer
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { KeyboardArrowDown, Menu as MenuIcon } from "@mui/icons-material";
import { Logo } from "./logo";
import swal from "sweetalert";

// THEME DÀNH RIÊNG CHO NAVBAR (Giữ độ mỏng nhẹ, sang trọng)
const navTheme = createTheme({
    typography: { fontFamily: "'Plus Jakarta Sans', sans-serif" },
});
const EMPTY_ROLES = Object.freeze({});

export const DashboardSidebar = (props) => {
    const location = useLocation();
    const currentPath = location.pathname;
    const navigate = useNavigate(); 

    const [profile, setProfile] = useState(JSON.parse(localStorage.getItem("profile")));

    // --- STATE QUẢN LÝ MENU TRÊN MÁY TÍNH ---
    const [anchorElUser, setAnchorElUser] = useState(null);
    const [anchorElTX, setAnchorElTX] = useState(null); 
    const [anchorElXD, setAnchorElXD] = useState(null);
    const [anchorElXS, setAnchorElXS] = useState(null);
    const [anchorElHistory, setAnchorElHistory] = useState(null);

    // --- STATE QUẢN LÝ MENU ĐIỆN THOẠI ---
    const [mobileOpen, setMobileOpen] = useState(false);

    // ==========================================
    // KIỂM TRA PHÂN QUYỀN MỚI TỪ PROFILE
    // ==========================================
    const isFullAdmin = profile?.permission?.full === 1;
    const pRoles = profile?.roles || EMPTY_ROLES;

    // ==========================================
    // BẢO VỆ ĐƯỜNG DẪN TỔNG (GLOBAL GUARD)
    // ==========================================
    useEffect(() => {
        // Nếu là Admin tối cao thì được đi muôn nơi, không cần kiểm tra
        if (isFullAdmin) return;

        // Định nghĩa bản đồ: Đường dẫn nào cần Quyền gì
        const routePermissions = {
            '/admin/users': pRoles.user_customer,
            '/admin/settx45s': pRoles.game_tx45,
            '/admin/settx60s': pRoles.game_tx60,
            '/admin/setxs75s': pRoles.game_xs75,
            '/admin/setxs120s': pRoles.game_xs120,
            '/admin/setxd45s': pRoles.game_xd45,
            '/admin/setxd60s': pRoles.game_xd60,
            '/admin/setbcr': pRoles.game_baccarat,
            '/admin/history': pRoles.hist_bet,
            '/admin/setting': pRoles.sys_odds,
            '/admin/dg2-keys': profile?.isAdmin === true,
            '/admin/mongo-manager': profile?.isAdmin === true,
        };

        // Nếu currentPath nằm trong danh sách kiểm tra, VÀ quyền trả về false -> Đuổi
        if (routePermissions[currentPath] === false) {
            swal("Truy cập bị từ chối!", "Tài khoản của bạn không được cấp quyền để xem nội dung này.", "error");
            navigate("/admin");
        }
    }, [currentPath, isFullAdmin, pRoles, profile?.isAdmin, navigate]);

    useEffect(() => {
        const timer = setInterval(() => {
            const storedProfile = JSON.parse(localStorage.getItem("profile"));
            if (storedProfile && !profile) {
                setProfile(storedProfile);
                clearInterval(timer); 
            }
        }, 1000); 
        return () => clearInterval(timer);
    }, [profile]);

    const handleCloseAll = () => {
        setAnchorElUser(null);
        setAnchorElTX(null);
        setAnchorElXD(null);
        setAnchorElXS(null);
        setAnchorElHistory(null);
    };

    // ==========================================
    // HÀM TẠO STYLE CHO NÚT MENU (Có hiệu ứng Active)
    // ==========================================
    const getNavButtonStyle = (isActive) => ({
        color: isActive ? '#1B84FF' : '#4B5675',
        fontWeight: isActive ? 800 : 600,
        textTransform: 'none',
        fontSize: '0.9rem', 
        px: 1.5,
        py: 1,
        borderRadius: '8px',
        whiteSpace: 'nowrap', 
        minWidth: 'max-content',
        backgroundColor: isActive ? '#F1FAFF' : 'transparent',
        transition: 'all 0.2s',
        '&:hover': { color: '#1B84FF', backgroundColor: '#F1FAFF' } 
    });

    const menuItemStyle = {
        py: 1.2, px: 3, fontSize: '0.85rem', fontWeight: 600, color: '#4B5675', whiteSpace: 'nowrap',
        '&:hover': { backgroundColor: '#F1FAFF', color: '#1B84FF' }
    };

    // --- COMPONENT HỖ TRỢ CHO MENU ĐIỆN THOẠI ---
    const MobileNavGroup = ({ title, children }) => (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#A1A5B7', fontWeight: 800, px: 2, mb: 0.5, display: 'block', textTransform: 'uppercase' }}>
                {title}
            </Typography>
            <Stack spacing={0.5}>{children}</Stack>
        </Box>
    );

    const MobileNavItem = ({ to, title }) => {
        const isActive = currentPath === to;
        return (
            <Button component={Link} to={to} onClick={() => setMobileOpen(false)}
                sx={{
                    justifyContent: 'flex-start', color: isActive ? '#1B84FF' : '#4B5675', 
                    fontWeight: isActive ? 800 : 600, textTransform: 'none',
                    px: 2, py: 1, borderRadius: '8px', mx: 1,
                    backgroundColor: isActive ? '#F1FAFF' : 'transparent',
                    '&:hover': { backgroundColor: '#F1FAFF', color: '#1B84FF' }
                }}
            >
                {title}
            </Button>
        );
    };

    return (
        <ThemeProvider theme={navTheme}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
                * { font-family: 'Plus Jakarta Sans', sans-serif; }
            `}</style>

            <AppBar position="sticky" elevation={0} sx={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E4E6EF', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <Container maxWidth="xl" sx={{ px: { xs: 2, xl: 5 } }}> 
                    <Toolbar disableGutters sx={{ minHeight: '70px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                        {/* === KHU VỰC TRÁI: LOGO === */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={() => setMobileOpen(true)}
                                sx={{ mr: 1, display: { lg: 'none' }, color: '#252F4A' }}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                <Logo sx={{ height: 35, width: 35 }} />
                            </Box>
                        </Box>

                        {/* === KHU VỰC GIỮA: CÁC MENU === */}
                        <Box sx={{ display: { xs: 'none', lg: 'flex' }, flexGrow: 1, justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                            <Button component={Link} to="/admin" sx={getNavButtonStyle(currentPath === '/admin')}>Tổng quan</Button>

                            {/* KHÁCH HÀNG & NHÂN VIÊN */}
                            {(isFullAdmin || pRoles.user_customer) && (
                                <>
                                    <Button 
                                        sx={getNavButtonStyle(currentPath === '/admin/users')} 
                                        endIcon={<KeyboardArrowDown />} 
                                        onClick={(e) => setAnchorElUser(e.currentTarget)}
                                    >
                                        Người Chơi
                                    </Button>
                                    <Menu anchorEl={anchorElUser} open={Boolean(anchorElUser)} onClose={handleCloseAll} PaperProps={{ sx: { mt: 2, borderRadius: '8px', minWidth: 200, boxShadow: '0px 10px 30px rgba(0,0,0,0.08)' } }}>
                                        <MenuItem component={Link} to="/admin/users" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/users' ? '#1B84FF' : '#4B5675'}}>Danh sách người chơi</MenuItem>
                                    </Menu>
                                </>
                            )}

                            {/* TÀI XỈU */}
                            {(isFullAdmin || pRoles.game_tx45 || pRoles.game_tx60) && (
                                <>
                                    <Button 
                                        sx={getNavButtonStyle(currentPath.includes('settx'))} 
                                        endIcon={<KeyboardArrowDown />} 
                                        onClick={(e) => setAnchorElTX(e.currentTarget)}
                                    >
                                        Tài Xỉu
                                    </Button>
                                    <Menu anchorEl={anchorElTX} open={Boolean(anchorElTX)} onClose={handleCloseAll} PaperProps={{ sx: { mt: 2, borderRadius: '8px', minWidth: 160, boxShadow: '0px 10px 30px rgba(0,0,0,0.08)' } }}>
                                        {(isFullAdmin || pRoles.game_tx45) && <MenuItem component={Link} to="/admin/settx45s" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/settx45s' ? '#1B84FF' : '#4B5675'}}>Tài Xỉu 45s</MenuItem>}
                                        {(isFullAdmin || pRoles.game_tx60) && <MenuItem component={Link} to="/admin/settx60s" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/settx60s' ? '#1B84FF' : '#4B5675'}}>Tài Xỉu 60s</MenuItem>}
                                    </Menu>
                                </>
                            )}

                            {(isFullAdmin || pRoles.game_xd45 || pRoles.game_xd60) && (
                                <>
                                    <Button
                                        sx={getNavButtonStyle(currentPath.includes('setxd'))}
                                        endIcon={<KeyboardArrowDown />}
                                        onClick={(e) => setAnchorElXD(e.currentTarget)}
                                    >
                                        Xóc Đĩa
                                    </Button>
                                    <Menu anchorEl={anchorElXD} open={Boolean(anchorElXD)} onClose={handleCloseAll} PaperProps={{ sx: { mt: 2, borderRadius: '8px', minWidth: 160, boxShadow: '0px 10px 30px rgba(0,0,0,0.08)' } }}>
                                        {(isFullAdmin || pRoles.game_xd45) && <MenuItem component={Link} to="/admin/setxd45s" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/setxd45s' ? '#1B84FF' : '#4B5675'}}>Xóc Đĩa 45s</MenuItem>}
                                        {(isFullAdmin || pRoles.game_xd60) && <MenuItem component={Link} to="/admin/setxd60s" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/setxd60s' ? '#1B84FF' : '#4B5675'}}>Xóc Đĩa 60s</MenuItem>}
                                    </Menu>
                                </>
                            )}

                            {(isFullAdmin || pRoles.game_xs75 || pRoles.game_xs120) && (
                                <>
                                    <Button
                                        sx={getNavButtonStyle(currentPath.includes('setxs'))}
                                        endIcon={<KeyboardArrowDown />}
                                        onClick={(e) => setAnchorElXS(e.currentTarget)}
                                    >
                                        Xổ Số
                                    </Button>
                                    <Menu anchorEl={anchorElXS} open={Boolean(anchorElXS)} onClose={handleCloseAll} PaperProps={{ sx: { mt: 2, borderRadius: '8px', minWidth: 160, boxShadow: '0px 10px 30px rgba(0,0,0,0.08)' } }}>
                                        {(isFullAdmin || pRoles.game_xs75) && <MenuItem component={Link} to="/admin/setxs75s" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/setxs75s' ? '#1B84FF' : '#4B5675'}}>Xổ Số 75s</MenuItem>}
                                        {(isFullAdmin || pRoles.game_xs120) && <MenuItem component={Link} to="/admin/setxs120s" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/setxs120s' ? '#1B84FF' : '#4B5675'}}>Xổ Số 120s</MenuItem>}
                                    </Menu>
                                </>
                            )}

                            {/* BACCARAT */}
                            {(isFullAdmin || pRoles.game_baccarat) && (
                                <>
                                    <Button component={Link} to="/admin/setbcr" sx={getNavButtonStyle(currentPath === '/admin/setbcr')}>Baccarat</Button>
                                </>
                            )}

                            {/* LỊCH SỬ CHƠI */}
                            {(isFullAdmin || pRoles.hist_bet) && (
                                <>
                                    <Button 
                                        sx={getNavButtonStyle(currentPath === '/admin/history')} 
                                        endIcon={<KeyboardArrowDown />} 
                                        onClick={(e) => setAnchorElHistory(e.currentTarget)}
                                    >
                                        Lịch Sử
                                    </Button>
                                    <Menu anchorEl={anchorElHistory} open={Boolean(anchorElHistory)} onClose={handleCloseAll} PaperProps={{ sx: { mt: 2, borderRadius: '8px', minWidth: 160, boxShadow: '0px 10px 30px rgba(0,0,0,0.08)' } }}>
                                        {(isFullAdmin || pRoles.hist_bet) && <MenuItem component={Link} to="/admin/history" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/history' ? '#1B84FF' : '#4B5675'}}>Lịch sử Cược</MenuItem>}
                                    </Menu>
                                </>
                            )}

                            {(isFullAdmin || pRoles.sys_odds) && (
                                <Button component={Link} to="/admin/setting" sx={getNavButtonStyle(currentPath === '/admin/setting')}>Tỷ lệ game</Button>
                            )}
                            {profile?.isAdmin === true && (
                                <Button component={Link} to="/admin/dg2-keys" sx={getNavButtonStyle(currentPath === '/admin/dg2-keys')}>API key DG2</Button>
                            )}
                            {profile?.isAdmin === true && (
                                <Button component={Link} to="/admin/mongo-manager" sx={getNavButtonStyle(currentPath === '/admin/mongo-manager')}>MongoDB</Button>
                            )}
                        </Box>

                        {/* === KHU VỰC BÊN PHẢI: ĐĂNG XUẤT === */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Button variant="outlined" 
                                onClick={() => {
                                    localStorage.removeItem("user"); localStorage.removeItem("profile"); localStorage.removeItem("data"); window.location.href = "/";
                                }}
                                sx={{ 
                                    display: { xs: 'none', sm: 'flex' }, 
                                    color: '#F1416C', borderColor: '#F1416C', 
                                    textTransform: 'none', fontWeight: 700, borderRadius: '8px', px: 3, 
                                    whiteSpace: 'nowrap', 
                                    '&:hover': { borderColor: '#F1416C', backgroundColor: '#FFF5F8' } 
                                }}
                            >
                                Đăng xuất
                            </Button>
                        </Box>

                    </Toolbar>
                </Container>

                {/* ======================================================== */}
                {/* DRAWER MENU TRƯỢT DÀNH RIÊNG CHO ĐIỆN THOẠI               */}
                {/* ======================================================== */}
                <Drawer
                    variant="temporary"
                    anchor="left"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }} 
                    sx={{
                        display: { xs: 'block', lg: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280, backgroundColor: '#FFFFFF' },
                    }}
                >
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Logo sx={{ height: 30, width: 30 }} />
                    </Box>
                    <Divider sx={{ mb: 2, borderColor: '#F4F6FA' }} />

                    <Box sx={{ flexGrow: 1, overflowY: 'auto', pb: 4 }}>
                        <MobileNavGroup title="Tổng quan">
                            <MobileNavItem to="/admin" title="Quản trị game" />
                        </MobileNavGroup>
                        {(isFullAdmin || pRoles.user_customer) && (
                            <MobileNavGroup title="Người chơi">
                                <MobileNavItem to="/admin/users" title="Danh sách người chơi" />
                            </MobileNavGroup>
                        )}

                        {(isFullAdmin || pRoles.game_tx45 || pRoles.game_tx60 || pRoles.game_xd45 || pRoles.game_xd60 || pRoles.game_xs75 || pRoles.game_xs120 || pRoles.game_baccarat) && (
                            <MobileNavGroup title="Quản Lý Trò Chơi">
                                {(isFullAdmin || pRoles.game_tx45 || pRoles.game_tx60) && (
                                    <>
                                        <Typography variant="body2" sx={{ px: 3, py: 1, color: '#252F4A', fontWeight: 'bold' }}>Tài Xỉu</Typography>
                                        <Box sx={{ pl: 1 }}>
                                            {(isFullAdmin || pRoles.game_tx45) && <MobileNavItem to="/admin/settx45s" title="Tài Xỉu 45s" />}
                                            {(isFullAdmin || pRoles.game_tx60) && <MobileNavItem to="/admin/settx60s" title="Tài Xỉu 60s" />}
                                        </Box>
                                    </>
                                )}

                                {(isFullAdmin || pRoles.game_xd45 || pRoles.game_xd60) && (
                                    <>
                                        <Typography variant="body2" sx={{ px: 3, py: 1, mt: 1, color: '#252F4A', fontWeight: 'bold' }}>Xóc Đĩa</Typography>
                                        <Box sx={{ pl: 1 }}>
                                            {(isFullAdmin || pRoles.game_xd45) && <MobileNavItem to="/admin/setxd45s" title="Xóc Đĩa 45s" />}
                                            {(isFullAdmin || pRoles.game_xd60) && <MobileNavItem to="/admin/setxd60s" title="Xóc Đĩa 60s" />}
                                        </Box>
                                    </>
                                )}

                                {(isFullAdmin || pRoles.game_xs75 || pRoles.game_xs120) && (
                                    <>
                                        <Typography variant="body2" sx={{ px: 3, py: 1, mt: 1, color: '#252F4A', fontWeight: 'bold' }}>Xổ Số</Typography>
                                        <Box sx={{ pl: 1 }}>
                                            {(isFullAdmin || pRoles.game_xs75) && <MobileNavItem to="/admin/setxs75s" title="Xổ Số 75s" />}
                                            {(isFullAdmin || pRoles.game_xs120) && <MobileNavItem to="/admin/setxs120s" title="Xổ Số 120s" />}
                                        </Box>
                                    </>
                                )}

                                {/* BACCARAT */}
                                {(isFullAdmin || pRoles.game_baccarat) && (
                                    <>
                                        <Typography variant="body2" sx={{ px: 3, py: 1, mt: 1, color: '#252F4A', fontWeight: 'bold' }}>Baccarat</Typography>
                                        <Box sx={{ pl: 1 }}>
                                            {(isFullAdmin || pRoles.game_baccarat) && <MobileNavItem to="/admin/setbcr" title="Baccarat" />}
                                        </Box>
                                    </>
                                )}
                            </MobileNavGroup>
                        )}

                        {(isFullAdmin || pRoles.hist_bet) && (
                            <MobileNavGroup title="Lịch Sử">
                                {(isFullAdmin || pRoles.hist_bet) && <MobileNavItem to="/admin/history" title="Lịch sử Cược" />}
                            </MobileNavGroup>
                        )}

                        {(isFullAdmin || pRoles.sys_odds) && (
                            <MobileNavGroup title="Cài đặt game">
                                <MobileNavItem to="/admin/setting" title="Tỷ lệ game" />
                            </MobileNavGroup>
                        )}
                        {profile?.isAdmin === true && (
                            <MobileNavGroup title="API">
                                <MobileNavItem to="/admin/dg2-keys" title="Quản lý API key DG2" />
                                <MobileNavItem to="/admin/mongo-manager" title="Quản lý MongoDB" />
                            </MobileNavGroup>
                        )}
                        <Divider sx={{ my: 2, borderColor: '#F4F6FA' }} />
                        <Button 
                            fullWidth
                            onClick={() => {
                                localStorage.removeItem("user"); localStorage.removeItem("profile"); localStorage.removeItem("data"); window.location.href = "/";
                            }}
                            sx={{ color: '#F1416C', fontWeight: 800, textTransform: 'none', justifyContent: 'flex-start', px: 3, '&:hover': { backgroundColor: '#FFF5F8' } }}
                        >
                            Đăng xuất
                        </Button>
                    </Box>
                </Drawer>

            </AppBar>
        </ThemeProvider>
    );
};